<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { api } from '@/api/client.js'
import { EP } from '@/api/endpoints.js'
import HeadlineFraunces from '@/design/primitives/HeadlineFraunces.vue'
import CoinGlyph from '@/design/primitives/CoinGlyph.vue'

import AccountSection     from './Profile/parts/AccountSection.vue'
import OrdersSection      from './Profile/parts/OrdersSection.vue'
import TopupsSection      from './Profile/parts/TopupsSection.vue'
import WithdrawalsSection from './Profile/parts/WithdrawalsSection.vue'
import InventorySection   from './Profile/parts/InventorySection.vue'
import DeliveriesSection  from './Profile/parts/DeliveriesSection.vue'
import SettingsSection    from './Profile/parts/SettingsSection.vue'

const router = useRouter()
const route  = useRoute()

// ——————————————————————
// Auth
// ——————————————————————
const user        = ref(null)
const authLoading = ref(true)
const authError   = ref(null)

onMounted(async () => {
  try {
    const res = await api.get(EP.authUser)
    if (!res.data?.user) {
      return router.push('/register?next=/profile')
    }
    user.value = res.data.user
  } catch (e) {
    if (e.status === 401) {
      router.push('/register?next=/profile')
    } else {
      authError.value = e.message || 'Authentication failed.'
    }
  } finally {
    authLoading.value = false
  }
})

// Fetch withdrawals enabled flag so we can hide the AT tab when admin disabled it.
onMounted(async () => {
  try {
    const { data } = await api.get('/api/withdrawals/config')
    withdrawalsEnabled.value = data?.enabled !== false
  } catch {
    withdrawalsEnabled.value = false
  }
  // If user landed on AT tab while it's disabled, fall back to account.
  if (!withdrawalsEnabled.value && activeSection.value === 'withdrawals') {
    activeSection.value = 'account'
  }
})

// ——————————————————————
// Navigation
// ——————————————————————
const ALL_TABS = [
  { key: 'account',     label: 'ACCOUNT'           },
  { key: 'orders',      label: 'ORDERS'            },
  { key: 'topups',      label: 'TOP-UPS'           },
  { key: 'withdrawals', label: 'ACTIVITY TOKENS'   },
  { key: 'inventory',   label: 'INVENTORY'         },
  { key: 'deliveries',  label: 'DELIVERIES'        },
  { key: 'settings',    label: 'SETTINGS'          },
]

const withdrawalsEnabled = ref(true)
const TABS = computed(() =>
  withdrawalsEnabled.value ? ALL_TABS : ALL_TABS.filter(t => t.key !== 'withdrawals')
)

const VALID_TABS = computed(() => TABS.value.map(t => t.key))
const initialTab = ALL_TABS.map(t => t.key).includes(route.query.tab) ? route.query.tab : 'account'
const activeSection = ref(initialTab)

// React to header AT button navigation (?tab=withdrawals) — happens when user
// clicks the AT pill while already on /profile and the page is not remounted.
watch(() => route.query.tab, (next) => {
  if (typeof next === 'string' && VALID_TABS.value.includes(next)) {
    activeSection.value = next
  }
})

const activeSectionComponent = computed(() => {
  switch (activeSection.value) {
    case 'account':     return AccountSection
    case 'orders':      return OrdersSection
    case 'topups':      return TopupsSection
    case 'withdrawals': return WithdrawalsSection
    case 'inventory':   return InventorySection
    case 'deliveries':  return DeliveriesSection
    case 'settings':    return SettingsSection
    default:            return AccountSection
  }
})

function activate(key) {
  activeSection.value = key
}

// Top-up: dispatch a global event picked up by AppHeader's TopUpModal
function openTopUp() {
  window.dispatchEvent(new CustomEvent('open-topup'))
}

// ——————————————————————
// Display name helpers
// ——————————————————————
const displayName = computed(() => {
  if (!user.value) return ''
  const name = user.value.displayName?.trim()
  if (name) return name
  const email = user.value.email || ''
  return email.split('@')[0] || 'PLAYER'
})

const balance = computed(() => {
  if (!user.value) return '0.00'
  return (parseFloat(user.value.balance) || 0).toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
})
</script>

<template>
  <!-- Auth loading -->
  <div v-if="authLoading" class="dossier-gate" aria-live="polite" aria-label="Authenticating">
    <span class="dossier-spinner" aria-hidden="true" />
    <span class="dossier-gate__text">ONE MOMENT…</span>
  </div>

  <!-- Auth error (non-401) -->
  <div v-else-if="authError" class="dossier-gate dossier-gate--err" aria-live="assertive">
    <p class="dossier-gate__text">{{ authError }}</p>
  </div>

  <!-- Authenticated view -->
  <div v-else-if="user" class="dossier u-rise-in">

    <!-- ▒▒ HEADER BAND — same shape as Market/Trading ▒▒ -->
    <section class="dossier__header">
      <div class="dossier__header-inner">
        <p class="dossier__kicker u-display u-allcaps">EVOLUXU · YOUR DOSSIER</p>
        <HeadlineFraunces level="h1">{{ displayName }}</HeadlineFraunces>
        <div class="dossier__balance" aria-label="Your balance">
          <CoinGlyph :size="22" />
          <span class="dossier__balance-num u-mono u-gold">{{ balance }}</span>
          <span class="dossier__balance-label u-display u-allcaps">EVOGEMS</span>
          <button class="dossier__topup u-display u-allcaps" type="button" @click="openTopUp">
            <span class="dossier__topup-plus">+</span>
            <span>Top Up</span>
          </button>
        </div>
      </div>
    </section>

    <!-- ▒▒ TAB STRIP — glass band like Market/Trading filter ▒▒ -->
    <nav class="dossier__tabs" aria-label="Profile sections">
      <div class="dossier__tabs-inner">
        <ul class="gold-rail__list" role="list">
          <li v-for="tab in TABS" :key="tab.key" class="gold-rail__item">
            <button
              class="tab"
              :class="{ 'tab--active': activeSection === tab.key }"
              :aria-current="activeSection === tab.key ? 'page' : undefined"
              @click="activate(tab.key)"
            >
              {{ tab.label }}
            </button>
          </li>
        </ul>
      </div>
    </nav>

    <!-- ▒▒ SECTION CONTENT ▒▒ -->
    <main class="dossier__main">
      <div class="dossier__main-inner">
        <Transition name="section-rise" mode="out-in">
          <component
            :is="activeSectionComponent"
            :key="activeSection"
            v-bind="
              activeSection === 'account' ? { user }
              : activeSection === 'withdrawals' ? { activityTokens: user?.activity_tokens || 0, user }
              : activeSection === 'settings' ? { withdrawalsEnabled }
              : {}
            "
          />
        </Transition>
      </div>
    </main>

  </div>
</template>

<style scoped>
/* ——————————————————————————————————
   Page root
   —————————————————————————————————— */
.dossier {
  display: flex;
  flex-direction: column;
  gap: 0;
  min-height: calc(100dvh - var(--header-h));
}

/* ——————————————————————————————————
   Header band — same shape as Market/Trading
   —————————————————————————————————— */
.dossier__header {
  min-height: 30vh;
  display: flex;
  align-items: flex-end;
  padding: var(--sp-24) var(--sp-6) var(--sp-12);
  background: linear-gradient(
    to bottom,
    transparent 0%,
    rgba(11, 10, 8, 0.6) 70%,
    rgba(11, 10, 8, 0.88) 100%
  );
}
.dossier__header-inner {
  max-width: var(--container-wide);
  margin: 0 auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--sp-3);
}
.dossier__kicker {
  margin: 0;
  font-size: var(--fs-12);
  color: var(--gold);
  letter-spacing: 0.22em;
}
.dossier__balance {
  display: inline-flex;
  align-items: center;
  gap: var(--sp-2);
  margin-top: var(--sp-2);
  padding: 8px 16px;
  border: 1px solid var(--gold);
  border-radius: var(--r-xs);
  background: rgba(11, 10, 8, 0.45);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}
.dossier__balance-num {
  font-size: var(--fs-21);
  font-family: var(--font-mono);
  color: var(--gold);
  letter-spacing: 0.04em;
}
.dossier__topup {
  /* Gold pill matching the height of the balance plate (the plate has 8px y-padding + 21px font, so ~37px tall) */
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: var(--sp-2);
  height: 32px;
  padding: 0 14px;
  border-radius: var(--r-xs);
  border: 1px solid #8C6E2A;
  background: linear-gradient(180deg, #F5DC97 0%, #E5C76B 55%, #C19F4F 100%);
  color: var(--ink-inverse, #1a1a1a);
  font-size: var(--fs-12);
  letter-spacing: 0.12em;
  cursor: pointer;
  transition: filter var(--t-fast) var(--ease-cinema);
}
.dossier__topup:hover { filter: brightness(1.08); }
.dossier__topup-plus { font-size: 16px; font-weight: 700; line-height: 1; margin-top: -1px; }

.dossier__balance-label {
  font-size: var(--fs-12);
  color: var(--ink-soft);
  letter-spacing: 0.18em;
}

/* ——————————————————————————————————
   Tab strip — glass band like Market/Trading filter
   —————————————————————————————————— */
.dossier__tabs {
  position: relative;
  z-index: 1;
  background: var(--bg-glass-strong);
  backdrop-filter: blur(12px) saturate(140%);
  -webkit-backdrop-filter: blur(12px) saturate(140%);
  border-bottom: 1px solid var(--ash);
  box-shadow: var(--shadow-1);
}
.dossier__tabs-inner {
  max-width: var(--container-wide);
  margin: 0 auto;
  padding: var(--sp-2) var(--sp-6);
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}
.dossier__tabs-inner::-webkit-scrollbar { display: none; }
.gold-rail__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 0;
  white-space: nowrap;
  justify-content: center;
}
.gold-rail__sep {
  color: var(--gold);
  font-size: var(--fs-12);
  opacity: 0.5;
  padding: 0 var(--sp-3);
  user-select: none;
}

/* ——————————————————————————————————
   Main content area
   —————————————————————————————————— */
.dossier__main {
  padding: var(--sp-12) 0 var(--sp-16);
  flex: 1;
}
.dossier__main-inner {
  max-width: var(--container-wide);
  margin: 0 auto;
  padding: 0 var(--sp-6);
}

.gold-rail__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 0;
  white-space: nowrap;
}

.gold-rail__sep {
  color: var(--gold);
  font-size: var(--fs-12);
  opacity: 0.5;
  padding: 0 var(--sp-3);
  user-select: none;
}

.gold-rail__item { display: flex; }

.tab {
  position: relative;
  padding: var(--sp-3) var(--sp-4);
  font-family: var(--font-display);
  font-size: var(--fs-14);
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--ink-soft);
  background: none;
  border: none;
  cursor: pointer;
  transition: color var(--t-fast) var(--ease-cinema);
  outline: none;
}

.tab:hover { color: var(--ink); }
.tab--active { color: var(--gold); }

.tab:focus-visible {
  outline: 2px solid var(--gold);
  outline-offset: 2px;
  border-radius: var(--r-xs);
}

/* ——————————————————————————————————
   Section content panel
   —————————————————————————————————— */
.panel {
  flex: 1;
  min-width: 0;
}

/* ——————————————————————————————————
   Section-switch transition
   —————————————————————————————————— */
.section-rise-enter-active {
  transition: opacity var(--t-base) var(--ease-cinema),
              transform var(--t-base) var(--ease-cinema);
}
.section-rise-leave-active {
  transition: opacity var(--t-fast) var(--ease-cinema);
}
.section-rise-enter-from {
  opacity: 0;
  transform: translateY(8px);
}
.section-rise-leave-to {
  opacity: 0;
}

/* ——————————————————————————————————
   Auth-gate overlay
   —————————————————————————————————— */
.dossier-gate {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--sp-4);
  min-height: calc(100vh - var(--header-h));
}

.dossier-gate--err { color: var(--ember); }

.dossier-gate__text {
  font-family: var(--font-display);
  font-size: var(--fs-14);
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: var(--ink-soft);
}

.dossier-gate--err .dossier-gate__text {
  color: var(--ember);
}

.dossier-spinner {
  display: inline-block;
  width: 36px;
  height: 36px;
  border: 2px solid var(--ash);
  border-top-color: var(--gold);
  border-radius: 50%;
  animation: spin 1.2s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

/* ——————————————————————————————————
   Mobile — horizontal scroll tabs + stacked header
   —————————————————————————————————— */
@media (max-width: 760px) {
  /* Header + tabs stay edge-to-edge so the gradient and ash divider span
     the full viewport width. Inner padding is owned by *_inner blocks. */
  .dossier__header { padding: var(--sp-16) var(--sp-4) var(--sp-8); }
  .dossier__main   { padding: var(--sp-8) 0 var(--sp-12); }
  .dossier__main-inner { padding: 0 var(--sp-4); }
  .dossier__tabs-inner { padding: var(--sp-2) var(--sp-4); }

  .gold-rail__list {
    /* snap on mobile */
    scroll-snap-type: x mandatory;
  }

  .gold-rail__item {
    scroll-snap-align: start;
  }
}

@media (max-width: 480px) {
  .tab {
    font-size: var(--fs-12);
    padding: var(--sp-2) var(--sp-3);
  }

  .gold-rail__sep {
    padding: 0 var(--sp-2);
  }
}
</style>
